import { SystemSuppliersTabProps } from "./SystemSuppliersTab.tsx";
import { SupplierAgreementViewModel, SystemSupplierTabRowViewModel, SystemSupplierTabViewModel, SystemSupplierType } from "./SystemSuppliersTab.types.ts";
import { useTranslation } from "../../../localization/useTranslation.ts";
import { useDocumentPreviewManager } from "@common/components/documentPreview/useDocumentPreviewManager";
import { EntityType } from "../../../common/Common.types.ts";
import { FilterOption, FilterOptionType } from "@common/components/filterPopover/FilterPopover.types.ts";
import { getUniqueFilterOptions, sortByName } from "@common/hooks/filterUtils.ts";
import { useFilterState } from "@common/hooks/useFilterState.ts";
import { applyClientFilters, withOperators } from "@common/components/filterBar/filterOperatorMatching.ts";
import { FilterOperatorUi } from "@common/api/filterTypes.ts";

import { useDotLegalQuery } from "@common/hooks/dotLegalReactQuery";
import { useNavigate } from "react-router";
import { useUrlProvider } from "../../../useUrlProvider.ts";
import { DocumentPreviewable } from "@common/components/documentPreview/DocumentPreview.types";

export function useSystemSuppliersTab(props: SystemSuppliersTabProps) {
    const { translateString, translateCountry } = useTranslation();
    const navigate = useNavigate();
    const { getLegalEntityUrl } = useUrlProvider();

    const {
        selectFilters: filters,
        filterOperators,
        onFilterChange,
        pinnedFilterIds,
        onPinnedFilterIdsChange,
    } = useFilterState(`system-${props.systemId}-suppliers-filters`);

    const url = `/SystemOverview/${props.systemId}/suppliers`;
    const { isLoading, data } = useDotLegalQuery<SystemSupplierTabViewModel>(url);

    const allAgreements: Array<DocumentPreviewable> = (data?.suppliers ?? []).flatMap((supplier) =>
        supplier.agreements.map((agreement) => ({
            documentId: agreement.documentId,
            name: agreement.name,
            fileName: agreement.fileName ?? undefined,
            link: agreement.link ?? undefined,
            dataId: agreement.dataId ?? undefined,
            entityId: supplier.id,
            entityType: EntityType.LegalEntityDocument,
            entityTitle: supplier.name,
        }))
    );

    const { previewableDocuments, handleDocumentWithTypeAndIdClick } = useDocumentPreviewManager({
        documents: allAgreements,
        entityType: EntityType.LegalEntityDocument,
    });

    function getTableData(data: Array<SystemSupplierTabRowViewModel>) {
        return data.map((x) => {
            return {
                ...x,
                typeString:
                    x.type === SystemSupplierType.SubContractor
                        ? `${translateString("secondarySupplier")}${x.supplierName ? ` (${x.supplierName})` : ""}`
                        : translateString("supplier"),
                countryString: translateCountry(x.countryCode) ?? x.countryCode,
            };
        });
    }

    function onAgreementClick(row: SystemSupplierTabRowViewModel, agreement: SupplierAgreementViewModel) {
        const previewable: DocumentPreviewable = {
            documentId: agreement.documentId,
            name: agreement.name,
            fileName: agreement.fileName ?? undefined,
            link: agreement.link ?? undefined,
            dataId: agreement.dataId ?? undefined,
        };
        handleDocumentWithTypeAndIdClick(previewable, EntityType.LegalEntityDocument, row.id);
    }

    function onNameClick(row: SystemSupplierTabRowViewModel) {
        if (row.isLink) {
            const url = row.name.startsWith("http://") || row.name.startsWith("https://") ? row.name : `https://${row.name}`;
            window.open(url, "_blank");
        } else {
            navigate(getLegalEntityUrl(row.id));
        }
    }

    const allRows = getTableData(data?.suppliers ?? []);
    const filteredRows = applyClientFilters(
        allRows,
        [
            { id: "supplier", candidate: (r) => r.name },
            { id: "type", candidate: (r) => r.type.toString() },
            { id: "country", candidate: (r) => r.countryCode },
        ],
        filters,
        {},
        filterOperators,
        (row) => {
            if ((filters.agreement?.length ?? 0) === 0) return true;

            const hasAgreementEvidence =
                row.agreements.length > 0 || row.contracts.length > 0 || row.containsNotAccessibleDocuments || row.containsNotAccessibleContracts;
            const matchesYes = filters.agreement.includes("yes") && (hasAgreementEvidence || row.hasDataProcessorAgreement);
            const matchesNo = filters.agreement.includes("no") && !hasAgreementEvidence && !row.hasDataProcessorAgreement;
            const matchesAgreement = row.agreements.some((a) => filters.agreement.includes(`agreement-${a.documentId}`));
            const matchesContract = row.contracts.some((c) => filters.agreement.includes(`contract-${c.id}`));

            const hasAnyMatch = matchesYes || matchesNo || matchesAgreement || matchesContract;
            const isNegative = filterOperators.agreement === FilterOperatorUi.Negative;
            return isNegative ? !hasAnyMatch : hasAnyMatch;
        }
    )!;

    function getAgreementOptions() {
        const options = [
            { id: "yes", name: translateString("yes") },
            { id: "no", name: translateString("no") },
        ];

        const seen = new Set<string>();
        allRows.forEach((row) => {
            row.agreements.forEach((agreement) => {
                if (!seen.has(agreement.name)) {
                    seen.add(agreement.name);
                    options.push({ id: `agreement-${agreement.documentId}`, name: agreement.name });
                }
            });
            row.contracts.forEach((contract) => {
                if (!seen.has(contract.name)) {
                    seen.add(contract.name);
                    options.push({ id: `contract-${contract.id}`, name: contract.name });
                }
            });
        });

        return options;
    }

    function getFilterOptions(): Array<FilterOption> {
        return withOperators(
            [
                {
                    id: "supplier",
                    label: translateString("supplier"),
                    type: FilterOptionType.SelectField,
                    value: filters.supplier ?? [],
                    fixedData: getUniqueFilterOptions(allRows, (r) => r.name).sort(sortByName),
                },
                {
                    id: "type",
                    label: translateString("type"),
                    type: FilterOptionType.SelectField,
                    value: filters.type ?? [],
                    fixedData: [
                        { id: SystemSupplierType.Supplier.toString(), name: translateString("supplier") },
                        { id: SystemSupplierType.SubContractor.toString(), name: translateString("secondarySupplier") },
                    ],
                },
                {
                    id: "country",
                    label: translateString("country"),
                    type: FilterOptionType.SelectField,
                    value: filters.country ?? [],
                    fixedData: getUniqueFilterOptions(
                        allRows,
                        (r) => r.countryString,
                        (r) => r.countryCode ?? undefined
                    ).sort(sortByName),
                },
                {
                    id: "agreement",
                    label: translateString("agreement"),
                    type: FilterOptionType.SelectField,
                    value: filters.agreement ?? [],
                    fixedData: getAgreementOptions().sort(sortByName),
                },
            ],
            filterOperators
        );
    }

    const hasActiveFilters = Object.values(filters).some((v) => v.length > 0);

    return {
        pinnedFilterIds,
        onPinnedFilterIdsChange,
        isLoading,
        hasData: isLoading || allRows.length > 0,
        rows: filteredRows,
        onAgreementClick,
        onNameClick,
        previewableDocuments,
        getFilterOptions,
        onFilterChange,
        hasActiveFilters,
    };
}
